import React, { useState, useEffect, useRef, useCallback } from 'react';
import { isInNativeApp } from './platform';
import { collection, addDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { QRCodeCanvas } from 'qrcode.react';

// Card types configuration
import CelestialCardArt, { CARD_DESIGNS } from './components/CelestialCardArt';
import SwipeRail from './components/SwipeRail';

const CARD_TYPES = [
  { 
    id: 'card_1', 
    name: CARD_DESIGNS.card_1.name, 
    emoji: '🌺🌺🌺',
    image: '/assets/cards/card_1.png',
    price: 0.00,
    color: 'rose' 
  },
  { 
    id: 'card_2', 
    name: CARD_DESIGNS.card_2.name, 
    emoji: '💐', 
    image: '/assets/cards/card_2.png',
    price: 0.00,
    color: 'pink' 
  },
  { 
    id: 'card_3', 
    name: CARD_DESIGNS.card_3.name, 
    emoji: '🕯️', 
    image: '/assets/cards/card_3.png',
    price: 0.00,
    color: 'yellow' 
  },
  { 
    id: 'card_4', 
    name: CARD_DESIGNS.card_4.name, 
    emoji: '🌸', 
    image: '/assets/cards/card_4.png',
    price: 0.00,
    color: 'purple' 
  },
  { id: 'card_5', name: CARD_DESIGNS.card_5.name, emoji: '🕊️', image: '', price: 0.00, color: 'green' },
  { id: 'card_6', name: CARD_DESIGNS.card_6.name, emoji: '🌾', image: '', price: 0.00, color: 'sage' },
  { id: 'card_7', name: CARD_DESIGNS.card_7.name, emoji: '✦',  image: '', price: 0.00, color: 'gold' },
  { id: 'card_8', name: CARD_DESIGNS.card_8.name, emoji: '🏛️', image: '', price: 0.00, color: 'amber' },
];

// Helper function to get user display name
const getUserDisplayName = (user) => {
  if (!user) return 'Anonymous';
  return user.displayName || user.email?.split('@')[0] || 'Anonymous';
};

// Helper function to get card details
const getCardDetails = (cardType) => {
  return CARD_TYPES.find(f => f.id === cardType) || CARD_TYPES[0];
};

export default function DigitalCardsSection({ 
  memorial, 
  memorialId, 
  slug,
  auth, 
  db,
  handleLoginRedirect 
}) {
  const [cards, setCards] = useState([]);
  const [selectedCardType, setSelectedCardType] = useState(CARD_TYPES[0]);
  // Who the card is from. A card is often signed by a group — "His shipmates",
  // "The Pryce family" — not the account holder's own name, so the sender
  // chooses. Left empty, it falls back to the signed-in name.
  const [fromName, setFromName] = useState('');

  const senderName = () =>
    fromName.trim().slice(0, 60)
    || (auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous');
  // Cards are keepsakes, not headlines: every card sits one swipe away in a
  // snap rail, so the section never grows and no sender is hidden behind a
  // "show more" button. Dots mark position and jump straight to a card.
  const cardRailRef = useRef(null); // cards-rail-hide-scrollbar
  const [activeCard, setActiveCard] = useState(0);

  const handleCardScroll = useCallback(() => {
    const el = cardRailRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / Math.max(el.clientWidth, 1));
    setActiveCard((prev) => (prev === i ? prev : i));
  }, []);

  const scrollToCard = useCallback((i) => {
    const el = cardRailRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
    setActiveCard(i);
  }, []);

  // Subscribe to cards
  useEffect(() => {
    if (!memorialId || !memorial) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'cards'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setCards(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, memorial, db]);

  // Handle payment success
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      
      if (sessionId && memorialId) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verifyCardPayment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, memorialId })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to verify payment`);
          }

          const data = await response.json();
          
          if (data.success) {
            const pendingCard = sessionStorage.getItem('pendingCard');
            
            if (pendingCard) {
              const cardData = JSON.parse(pendingCard);
              
              await addDoc(collection(db, 'memorials', memorialId, 'cards'), {
                userName: cardData.userName,
                userId: auth.currentUser?.uid || 'anonymous',
                cardType: cardData.cardType,
                timestamp: new Date(),
                sessionId: sessionId,
                verified: true,
                paid: true
              });
              
              setSelectedCardType(CARD_TYPES[0]);
              scrollToCard(0);
              sessionStorage.removeItem('pendingCard');
              
              const cardName = CARD_TYPES.find(f => f.id === cardData.cardType)?.name || 'card';
              alert(`Thank you for your beautiful ${cardName}! 🌸 Your card will appear on the memorial shortly.`);
              
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
        }
      }
    };

    if (memorialId) {
      handlePaymentSuccess();
    }
  }, [memorialId, auth, db]);

  const handleGiveCard = async () => {
    if (isInNativeApp()) {
      alert('Tributes are purchased on the web — please visit soulemap.com in your browser.');
      return;
    }
    if (selectedCardType.price === 0) {
      try {
        await addDoc(collection(db, 'memorials', memorialId, 'cards'), {
          userName: senderName(),
          userId: auth.currentUser?.uid || 'anonymous',
          cardType: selectedCardType.id,
          timestamp: new Date(),
          verified: true,
          paid: false,
        });
        
        setSelectedCardType(CARD_TYPES[0]);
        setFromName('');
        scrollToCard(0);
        
        alert(`Thank you for your beautiful ${selectedCardType.name}! 🌸 Your card will appear on the memorial shortly.`);
        
      } catch (error) {
        console.error('Error placing free card:', error);
        alert('Error placing card. Please try again.');
      }
      return;
    }

    if (!window.Stripe) {
      alert('Stripe.js not loaded. Please refresh the page and try again.');
      return;
    }

    try {
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      if (!stripe) {
        throw new Error('Stripe not initialized properly');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/createCardSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          memorialId: slug,
          cardType: selectedCardType.id,
          price: selectedCardType.price,
          customerEmail: auth.currentUser?.email || 'anonymous@card.com',
          userName: senderName()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: Failed to create payment session`);
      }

      const { sessionId } = await response.json();
      
      if (!sessionId) {
        throw new Error('No session ID received from server');
      }

      sessionStorage.setItem('pendingCard', JSON.stringify({
        cardType: selectedCardType.id,
        userName: senderName(),
        memorialId: memorialId,
        timestamp: new Date().toISOString()
      }));

      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        sessionStorage.removeItem('pendingCard');
        alert(`Payment failed: ${error.message}`);
      }
      
    } catch (err) {
      console.error('❌ Card purchase error:', err);
      alert(`Error: ${err.message}`);
      sessionStorage.removeItem('pendingCard');
    }
  };

  return (
    <div id="cards-section" className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Words of Comfort
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Condolence Cards
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="mt-4 mb-6 text-gray-500 text-sm font-light">
          Written to the family they left behind
        </p>

        {/* Cards list */}
        <div className="space-y-6">
          {cards.length > 0 && (
          <h3 className="text-lg font-extralight text-gray-700 text-center mb-6" style={{ letterSpacing: '0.08em' }}>
            {cards.length} Card{cards.length !== 1 ? "s" : ""} Sent to the Family
          </h3>
          )}

          {cards.length > 0 ? (
            <div className="max-w-4xl mx-auto">
            <div
              ref={cardRailRef}
              onScroll={handleCardScroll}
              className="rail flex gap-4 overflow-x-auto pb-1"
              style={{
                scrollSnapType: 'x mandatory',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {cards.map((card, idx) => {
                const cardDetails = getCardDetails(card.cardType);
                const displayName = card.userName || "Anonymous";
                
                // Debug log
                
                
                
                

                return (
                  <div 
                    key={card.id || idx} 
                    className="relative rounded-2xl overflow-hidden border border-[#eee9df] bg-white shadow-lg hover:border-[#d8c9a4] hover:shadow-[0_14px_50px_rgba(184,150,79,0.16)] transition-all duration-300"
                    style={{ flex: '0 0 100%', scrollSnapAlign: 'center' }}
                  >
                    {/* Card art — rendered in the celestial theme */}
                    <div className="relative w-full aspect-[16/9]">
                      <div className="absolute inset-0" style={{ zIndex: 10 }}>
                        <CelestialCardArt type={cardDetails?.id || 'card_1'} />
                      </div>
                      
                      {/* QR Code - Bottom Left */}
                      <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4" style={{ zIndex: 20 }}>
                        <div className="bg-white p-1 md:p-1.5 rounded shadow-lg">
                          <QRCodeCanvas 
                            value={`${window.location.origin}/memorial/${memorial?.slug || slug}`}
                            size={32}
                            bgColor="#ffffff"
                            fgColor="#000000"
                            level="M"
                            className="w-8 h-8 md:w-10 md:h-10"
                          />
                        </div>
                      </div>
                      
                      {/* Signature — handwritten, no box, the way a real card is signed */}
                      <div className="absolute bottom-2 right-4 md:bottom-3 md:right-6 text-right pointer-events-none" style={{ zIndex: 20 }}>
                        <p
                          className="text-lg md:text-2xl leading-tight"
                          style={{
                            fontFamily: "'Snell Roundhand', 'Savoye LET', 'Apple Chancery', 'Segoe Script', 'Brush Script MT', cursive",
                            color: '#ffe2a8',
                            textShadow: '0 1px 8px rgba(5,4,13,0.9), 0 0 18px rgba(5,4,13,0.7)',
                            transform: 'rotate(-2deg)',
                          }}
                        >
                          — {displayName}
                        </p>
                        <p
                          className="text-[9px] md:text-[11px] uppercase mt-0.5"
                          style={{ color: 'rgba(255,255,255,0.45)', letterSpacing: '0.18em', textShadow: '0 1px 6px rgba(5,4,13,0.9)' }}
                        >
                          {card.timestamp?.toDate ?
                            card.timestamp.toDate().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) :
                            new Date(card.timestamp).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
                          }
                        </p>
                      </div>
                    </div>

                  </div>
                );
              })}
            </div>

            {/* Which card you're on — and a way to reach any of them directly */}
            {cards.length > 1 && (
              <div className="mt-4 text-center">
                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                  {cards.map((card, i) => (
                    <button
                      key={`dot-${card.id || i}`}
                      onClick={() => scrollToCard(i)}
                      aria-label={`Card ${i + 1} of ${cards.length}`}
                      className="p-1 leading-none"
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <span
                        className="block rounded-full"
                        style={{
                          width: i === activeCard ? '7px' : '5px',
                          height: i === activeCard ? '7px' : '5px',
                          background: i === activeCard ? '#b8964f' : 'rgba(184,150,79,0.3)',
                          transition: 'all 350ms ease',
                        }}
                      />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] uppercase text-gray-400" style={{ letterSpacing: '0.22em' }}>
                  {cards[activeCard]?.userName || 'Anonymous'} · {activeCard + 1} of {cards.length} · swipe to read them all
                </p>
              </div>
            )}
            </div>
          ) : (
            <div className="max-w-md mx-auto text-center py-6">
              <div
                className="mx-auto w-72 aspect-[16/9] rounded-xl overflow-hidden mb-5 opacity-80"
                style={{ boxShadow: '0 10px 40px rgba(184,150,79,0.18)' }}
              >
                <CelestialCardArt type="card_1" compact />
              </div>
              <p className="text-gray-500 text-sm font-light">
                No cards yet — {auth.currentUser ? 'be the first to send one to the family.' : 'sign in to send the first one.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Card placement form */}
      {auth.currentUser ? (
        <div className="max-w-3xl mx-auto mb-8">
          {/* Card type chooser */}
          <div className="mb-6">
            <p className="text-gray-600 text-lg font-light pb-1">
              Send a card to the family
            </p>
            <p className="text-gray-500 text-sm font-light pb-4">
              A flower is laid for {memorial?.firstName || 'them'} — a card carries words of comfort to those they left behind.
            </p>
            <SwipeRail>
              {CARD_TYPES.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCardType(card)}
                  style={{ scrollSnapAlign: 'start' }}
                  className={`relative shrink-0 basis-[46%] sm:basis-[31%] md:basis-[23%] border-2 rounded-xl transition-all duration-300 overflow-hidden min-h-[120px] md:min-h-[140px] group ${
                    selectedCardType.id === card.id
                      ? "border-[#cbab6a] shadow-xl ring-2 ring-[#e5c883]"
                      : "border-[#eee9df] hover:border-[#d8c9a4] hover:shadow-lg"
                  }`}
                >
                  <div className="absolute inset-0">
                    <CelestialCardArt type={card.id} compact />
                  </div>
                  <div className="relative z-10 flex flex-col items-center justify-end h-full">
                    <div className="text-center">
                      {card.price > 0 && (
                        <div className="text-xs font-light text-white bg-black backdrop-blur-sm px-3 py-1 rounded-full"
                          style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.6)' }}>
                          ${card.price}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedCardType.id === card.id && (
                    <div className="absolute top-2 right-2 z-20">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e5c883, #b8964f)' }}>
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </SwipeRail>
          </div>

          <div className="max-w-md mx-auto mt-6">
            <label className="block text-[11px] uppercase tracking-widest text-gray-500 mb-2 text-center">
              From
            </label>
            <input
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              maxLength={60}
              placeholder={auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Your name, or a family\u2019s'}
              className="w-full px-4 py-3 rounded-xl border border-[#e3ddcf] text-sm text-center bg-white focus:outline-none focus:border-[#cbab6a] transition-colors"
            />
            <p className="mt-2 text-[11px] text-gray-400 font-light text-center">
              How it will be signed &mdash; &ldquo;The Hale family&rdquo;, &ldquo;His shipmates&rdquo;.
              Left empty, your name signs it.
            </p>
          </div>

          <div className="flex justify-center mt-5">
            <button
              onClick={handleGiveCard}
              className="sm-btn-primary"
            >
              Send this card {selectedCardType.price > 0 ? `($${selectedCardType.price})` : ''}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center mb-8 bg-[#faf9f7] border border-[#eee9df] p-8 max-w-3xl mx-auto rounded-2xl">
          <div
            className="w-14 h-14 mx-auto mb-5 rounded-full flex items-center justify-center text-xl"
            style={{ background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)', boxShadow: '0 0 0 1px rgba(255,215,140,0.5), 0 0 20px rgba(255,215,140,0.25)', color: '#ffe2a8' }}
          >
            💌
          </div>
          <p className="text-gray-600 mb-4 text-lg font-light">
            Sign in to place a card on this memorial
          </p>
          <p className="text-gray-500 mb-6 text-sm">
            Honor their memory with a beautiful digital card
          </p>
          <button
            onClick={handleLoginRedirect}
            className="sm-btn-primary"
          >
            Log In to Place Card
          </button>
        </div>
      )}
    </div>
  );
}
